import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TokenStorageService } from '../../services/token-storage.service';
import { StandardResponseDTO } from 'src/app/interfaces/standardresponse';
import { MatDialog as MatDialog, MatDialogConfig as MatDialogConfig } from '@angular/material/dialog';
import { UserDTO } from 'src/app/interfaces/user';
import { createSettings, SettingsDTO } from 'src/app/interfaces/settings';
import { SettingsService } from 'src/app/services/settings.service';
import { catchError, EMPTY, firstValueFrom, Observable, switchMap, tap } from 'rxjs';
import { TimeZoneDTO } from 'src/app/interfaces/timeZone';
import { TimeZoneService } from 'src/app/services/time-zone.service';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { DatePipe, NgForOf, NgIf } from '@angular/common';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { CalendarIntegrationDTO } from 'src/app/interfaces/calendarIntegration';
import { AppRegistrationDTO } from 'src/app/interfaces/appRegistration';
import { AppRegistrationService } from 'src/app/services/app-registration.service';
import { CalendarIntegrationService } from 'src/app/services/calendar-integration.service';
import { CalendarIntegrationComponent } from '../linked-account/linked-account.component';
import { CreateConsentComponent } from '../create-consent/create-consent.component';
import { AppRegistrationComponent } from '../app-registration/app-registration.component';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-settings',
  imports: [MatCheckboxModule, FormsModule, MatLabel, MatFormFieldModule, MatOptionModule, MatSelectModule, MatIconModule, MatInputModule, MatButtonModule,
    NgForOf, MatGridListModule, MatTableModule, DatePipe, NgIf, MatTooltipModule
  ],
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
})
export class SettingsComponent implements OnInit {

  role: string;
  isOk: boolean = false;
  isLoggedIn = false;
  user: UserDTO;
  standardResponse: StandardResponseDTO;
  timeZones: TimeZoneDTO[] = [];
  timeZone: TimeZoneDTO;
  settings: SettingsDTO = { id: -1, userId: -1, timeZone: "Europe/Amsterdam", outlookHours: 20, lookbackHours: 2, keepDoneVisible: 20, revisionsEnabled: false, showOnHold: false, taskNotificationsEnabled: true, taskNotificationTime: '9:00', meetingNotificationLeadTime: 15, meetingNotificationsEnabled: true, showWelcomePopup: true };
  rowSpan: number = 1;
  rowCount: number = 8;
  rowHeight: number = 50;
  screenHeight: number;
  message: String;
  displayedColumns: string[] = ['Name', 'Account type' ];
  displayedARColumns: string[] = ['Description', 'Due Date Client Secret', 'Tasks'];
  dataSource = new MatTableDataSource<CalendarIntegrationDTO>();
  dsAppRegistrations = new MatTableDataSource<AppRegistrationDTO>();


  public filteredTimeZones: any[] = [];

  constructor(public matDialog: MatDialog, private settingsService: SettingsService, private tokenStorageService: TokenStorageService,
    private router: Router, private timeZoneService: TimeZoneService, private appRegistrationService: AppRegistrationService,
    private calendarIntegrationService: CalendarIntegrationService) {
  }

  isAuthorized() {
    this.isLoggedIn = !!this.tokenStorageService.getToken();
    if (this.isLoggedIn) {
      this.user = this.tokenStorageService.getUser();
      this.role = this.user.role;
      this.isOk = true;
    }
    if (!this.isOk) {
      this.router.navigate(['home']);
    }
  }

  ngOnInit(): void {
    this.isAuthorized();

    this.getData().subscribe();

    this.screenHeight = window.innerHeight;
    this.screenHeight = this.screenHeight - (this.rowCount * this.rowHeight);
    this.rowSpan = Math.floor(this.screenHeight / this.rowHeight);
  }

  getData(): Observable<any> {
    return this.timeZoneService.getTimeZones().pipe(

      tap({
        next: (data: any) => {
          this.timeZones = data;
          this.filteredTimeZones = data;
        }
      }),

      switchMap(() => {
        return this.appRegistrationService.getAppRegistrations();
      }),

      tap({
        next: (data: any) => {
          this.dsAppRegistrations.data = data;
        }
      }),

      switchMap(() => {
        return this.calendarIntegrationService.getCalendarIntegrations();
      }),

      tap({
        next: (data: any) => {
          this.dataSource.data = data;
        }
      }),

      switchMap(() => {
        return this.settingsService.getSettings();
      }),

      tap({
        next: (data: any) => {
          this.settings = data;
        }
      }),

      catchError(error => {
        console.error("One of the API calls failed:", error);
        return EMPTY; // stop the stream
      })

    );
  }

  filterTimezones(event: Event) {
    const query = (event.target as HTMLInputElement).value.toLowerCase();
    this.filteredTimeZones = this.timeZones.filter(tz =>
      tz.displayName.toLowerCase().includes(query) ||
      tz.id.toLowerCase().includes(query)
    );
  }

  async saveSettings() {
    try {
      const data = await firstValueFrom(this.settingsService.saveSettings(this.settings.id,
        createSettings(this.user.id, this.settings.timeZone, this.settings.outlookHours, this.settings.lookbackHours,
          this.settings.keepDoneVisible, this.settings.revisionsEnabled, this.settings.showOnHold, this.settings.taskNotificationsEnabled,
          this.settings.taskNotificationTime, this.settings.meetingNotificationsEnabled, this.settings.meetingNotificationLeadTime,
          this.settings.showWelcomePopup)));
      if (data) {
        this.settings = data;
        this.router.navigate(['/dashboard']);
      }
    } catch (error) {
    }
  }

  convertDataURIToBinary(dataURI) {
    var raw = window.atob(dataURI);
    var rawLength = raw.length;
    var array = new Uint8Array(new ArrayBuffer(rawLength));

    for (let i = 0; i < rawLength; i++) {
      array[i] = raw.charCodeAt(i);
    }
    return array;
  }

  openCalendarIntegrationModal(row) {
    const dialogConfig = new MatDialogConfig();
    // The user can't close the dialog by clicking outside its body
    dialogConfig.disableClose = false;
    dialogConfig.id = "calendarIntegration";
    dialogConfig.data = row;
    dialogConfig.height = "542px"; //678
    dialogConfig.width = "1008px";
    dialogConfig.panelClass = "squaredDialog";
    const modalDialog = this.matDialog.open(CalendarIntegrationComponent, dialogConfig);
    modalDialog.afterClosed().subscribe(data => {
      this.getData().subscribe();
    });
  }

  createNewCalendarIntegration() {
    const dialogConfig = new MatDialogConfig();
    // The user can't close the dialog by clicking outside its body
    dialogConfig.disableClose = false;
    dialogConfig.id = "modal-component";
    dialogConfig.data = this.dataSource.data;
    dialogConfig.height = "480px"; //678
    dialogConfig.width = "1008px"; //1440
    const modalDialog = this.matDialog.open(CreateConsentComponent, dialogConfig);
    modalDialog.afterClosed().subscribe(data => {
      this.getData().subscribe();
    });
  }

  removeFromList(calendarIntegration: CalendarIntegrationDTO, calendarIntegrations: CalendarIntegrationDTO[], replace: boolean) {
    var index = calendarIntegrations.findIndex(x => x.id == calendarIntegration.id);
    if (index > -1) {
      if (replace) {
        calendarIntegrations.splice(index, 1, calendarIntegration);
      } else {
        calendarIntegrations.splice(index, 1);
      }
    }
    this.dataSource.data = calendarIntegrations;
  }

  addToList(calendarIntegration: CalendarIntegrationDTO, calendarIntegrations: CalendarIntegrationDTO[]) {
    calendarIntegrations.push(calendarIntegration);
    this.dataSource.data = calendarIntegrations;
  }

  addToARList(appRegistration: AppRegistrationDTO, appRegistrations: AppRegistrationDTO[]) {
    appRegistrations.push(appRegistration);
    this.dsAppRegistrations.data = appRegistrations;
  }

  removeFromARList(appRegistration: AppRegistrationDTO, appRegistrations: AppRegistrationDTO[], replace: boolean) {
    var index = appRegistrations.findIndex(x => x.id == appRegistration.id);
    if (index > -1) {
      if (replace) {
        appRegistrations.splice(index, 1, appRegistration);
      } else {
        appRegistrations.splice(index, 1);
      }
    }
    this.dsAppRegistrations.data = appRegistrations;
  }

  createAppRegistration() {
    const dialogConfig = new MatDialogConfig();
    // The user can't close the dialog by clicking outside its body
    dialogConfig.disableClose = false;
    dialogConfig.id = "modal-component";
    dialogConfig.data = null;
    dialogConfig.height = "500px";
    dialogConfig.width = "500px";
    const modalDialog = this.matDialog.open(AppRegistrationComponent, dialogConfig);
    modalDialog.afterClosed().subscribe(data => {
      if (data != null) {
        this.addToARList(data, this.dsAppRegistrations.data);
      } else {
        this.getData();
      }
    });

  }

  openAppRegistration(appRegistration: AppRegistrationDTO) {
    const dialogConfig = new MatDialogConfig();
    // The user can't close the dialog by clicking outside its body
    dialogConfig.disableClose = false;
    dialogConfig.id = "modal-component";
    dialogConfig.data = appRegistration;
    dialogConfig.height = "500px";
    dialogConfig.width = "500px";
    const modalDialog = this.matDialog.open(AppRegistrationComponent, dialogConfig);
    modalDialog.afterClosed().subscribe(data => {
      if (data != null) {
        this.removeFromARList(data, this.dsAppRegistrations.data, true);
      } else {
        this.getData();
      }
    });
  }

  deleteAppRegistration(appRegistration: AppRegistrationDTO) {
    const dialogConfig = new MatDialogConfig();
    // The user can't close the dialog by clicking outside its body
    dialogConfig.disableClose = true;
    dialogConfig.id = "modal-component";
    dialogConfig.data = {
      title: "Delete App Registration",
      message: "Are you sure you want to delete this app registration?"
    }

    dialogConfig.height = "205px";
    dialogConfig.width = "500px";
    const confirmDialog = this.matDialog.open(ConfirmDialogComponent, dialogConfig)
    confirmDialog.afterClosed().subscribe(async result => {
      if (result == true) {
        const data = await firstValueFrom(this.appRegistrationService.deleteAppRegistration(appRegistration));
        if (data) {
          this.removeFromARList(appRegistration, this.dsAppRegistrations.data, false);
        }
      }
    });

  }

}